import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const MAX_HYDRATED_VIDEOS = 2;
const READY_TIMEOUT_MS = 15000;

class PetOptimumVideoScroll extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    if (this.initialized) return;

    this.initialized = true;
    this.cards = [...this.querySelectorAll('.po-video-reviews__card')];
    this.track = /** @type {HTMLElement} */ (this.refs.track);
    this.abortController = new AbortController();
    this.visibilityRatios = new Map(this.cards.map((card) => [card, 0]));
    this.hydratedAt = new Map();
    this.sectionVisible = false;
    this.activeCard = null;
    this.hydrationQueue = Promise.resolve();
    this.mediaControllers = new WeakMap();
    this.dragState = null;
    this.suppressClick = false;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const signal = this.abortController.signal;
    this.track.addEventListener('scroll', this.handleScroll, { passive: true, signal });
    this.track.addEventListener('keydown', this.handleTrackKeydown, { signal });
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.track.addEventListener('pointermove', this.handlePointerMove, { signal });
    this.track.addEventListener('pointerup', this.handlePointerUp, { signal });
    this.track.addEventListener('pointercancel', this.handlePointerUp, { signal });
    this.track.addEventListener('click', this.handleSuppressedClick, { capture: true, signal });
    this.addEventListener('click', this.handleControlClick, { signal });
    this.addEventListener('input', this.handleRangeInput, { signal });
    document.addEventListener('visibilitychange', this.handleDocumentVisibility, { signal });
    document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    this.reducedMotionQuery.addEventListener('change', this.handleMotionPreferenceChange, { signal });

    this.previousButton = this.refs.previousButton;
    this.nextButton = this.refs.nextButton;
    this.previousButton?.addEventListener('click', () => this.moveBy(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.moveBy(1), { signal });

    this.sectionObserver = new IntersectionObserver(this.handleSectionIntersection, {
      rootMargin: '0px',
      threshold: 0,
    });
    this.sectionObserver.observe(this);

    this.cardObserver = new IntersectionObserver(this.handleCardIntersection, {
      root: this.track,
      threshold: [0, 0.25, 0.5, 0.75, 0.95],
    });
    this.cards.forEach((card) => this.cardObserver.observe(card));

    this.resizeObserver = new ResizeObserver(this.updateOverflowState);
    this.resizeObserver.observe(this.track);
    this.updateOverflowState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.sectionObserver?.disconnect();
    this.cardObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.pauseAll();
    this.unloadAll();
    this.initialized = false;
    super.disconnectedCallback();
  }

  handleSectionIntersection = (entries) => {
    const entry = entries[0];
    this.sectionVisible = Boolean(entry?.isIntersecting);

    if (!this.sectionVisible) {
      this.pauseAll();
      this.unloadAll();
      return;
    }

    this.selectMostVisible();
  };

  handleCardIntersection = (entries) => {
    entries.forEach((entry) => {
      this.visibilityRatios.set(entry.target, entry.intersectionRatio);
    });

    this.selectMostVisible();
  };

  selectMostVisible() {
    if (!this.sectionVisible || document.hidden || !this.cards.length) return;

    let nextCard = null;
    let highestRatio = 0;

    this.cards.forEach((card) => {
      const ratio = this.visibilityRatios.get(card) || 0;
      if (ratio > highestRatio) {
        highestRatio = ratio;
        nextCard = card;
      }
    });

    if (!nextCard || highestRatio < 0.25) {
      this.pauseAll();
      return;
    }

    this.setActiveCard(nextCard);
  }

  setActiveCard(card, { userInitiated = false } = {}) {
    if (!(card instanceof HTMLElement)) return;

    const previousCard = this.activeCard;
    if (previousCard !== card) {
      this.pauseCard(previousCard);
      this.activeCard = card;
      this.cards.forEach((item) => item.classList.toggle('is-active', item === card));
    }

    if (userInitiated) {
      card.dataset.userPaused = 'false';
    }

    if (
      !prefersReducedMotion() &&
      this.sectionVisible &&
      !document.hidden &&
      card.dataset.userPaused !== 'true'
    ) {
      this.playCard(card);
    }
  }

  async playCard(card, { unmute = false } = {}) {
    if (!(card instanceof HTMLElement)) return;

    const video = await this.hydrateCard(card);
    if (!video || !card.isConnected || !this.isConnected || !this.sectionVisible || document.hidden) return;
    if (this.activeCard && this.activeCard !== card) return;

    this.activeCard = card;
    this.cards.forEach((item) => {
      if (item !== card) this.pauseCard(item);
      item.classList.toggle('is-active', item === card);
    });

    video.muted = !unmute;
    video.playsInline = true;
    this.setVolumeState(card, video);

    try {
      await video.play();
      card.dataset.userPaused = 'false';
      this.setPlayState(card, true);
      this.setMuteState(card, video.muted);
      this.setVolumeState(card, video);
      this.setStatus(card, video.muted ? 'Video playing muted' : 'Video playing with sound');
    } catch {
      video.muted = true;
      this.setMuteState(card, true);
      this.setVolumeState(card, video);
      try {
        await video.play();
        this.setPlayState(card, true);
        this.setStatus(card, 'Video playing muted');
      } catch {
        this.setPlayState(card, false);
        this.setStatus(card, 'Video is ready to play');
      }
    }
  }

  hydrateCard(card) {
    const existingVideo = card.querySelector('video');
    if (existingVideo instanceof HTMLVideoElement) {
      this.hydratedAt.set(card, performance.now());
      return Promise.resolve(existingVideo);
    }

    const task = async () => {
      if (!this.isConnected || !this.sectionVisible) return null;

      const currentVideo = card.querySelector('video');
      if (currentVideo instanceof HTMLVideoElement) return currentVideo;

      const template = card.querySelector('.po-video-reviews__video-template');
      const media = card.querySelector('.po-video-reviews__media');
      if (!(template instanceof HTMLTemplateElement) || !(media instanceof HTMLElement)) return null;

      card.classList.remove('has-error');
      card.classList.add('is-loading');
      const fragment = template.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) {
        this.setErrorState(card);
        return null;
      }

      video.autoplay = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-hidden', 'true');
      media.insertBefore(fragment, media.querySelector('.po-video-reviews__controls'));
      this.bindMediaControls(card, video);

      const loaded = await this.waitForVideo(video);
      if (!loaded || !video.isConnected || !this.isConnected || !this.sectionVisible) {
        this.removeVideo(card);
        if (this.isConnected && this.sectionVisible && !loaded) this.setErrorState(card);
        return null;
      }

      card.classList.remove('is-loading', 'has-error');
      card.classList.add('is-ready');
      this.updateSeekState(card, video);
      this.setVolumeState(card, video);
      this.hydratedAt.set(card, performance.now());
      this.enforceHydrationLimit(card);
      return video;
    };

    const queuedTask = this.hydrationQueue.then(task, task);
    this.hydrationQueue = queuedTask.then(
      () => undefined,
      () => undefined
    );
    return queuedTask;
  }

  waitForVideo(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        video.removeEventListener('loadedmetadata', handleReady);
        video.removeEventListener('canplay', handleReady);
        video.removeEventListener('error', handleError);
        video.removeEventListener('stalled', handleError);
        resolve(result);
      };
      const handleReady = () => finish(true);
      const handleError = () => finish(false);
      const timeout = window.setTimeout(() => finish(false), READY_TIMEOUT_MS);

      video.addEventListener('loadedmetadata', handleReady, { once: true });
      video.addEventListener('canplay', handleReady, { once: true });
      video.addEventListener('error', handleError, { once: true });
      video.addEventListener('stalled', handleError, { once: true });
      video.load();
    });
  }

  bindMediaControls(card, video) {
    const controller = new AbortController();
    const options = { signal: controller.signal };
    this.mediaControllers.set(video, controller);

    ['loadedmetadata', 'durationchange', 'timeupdate', 'seeking', 'seeked', 'ended'].forEach((eventName) => {
      video.addEventListener(eventName, () => this.updateSeekState(card, video), options);
    });
    video.addEventListener('volumechange', () => {
      this.setMuteState(card, video.muted || video.volume === 0);
      this.setVolumeState(card, video);
    }, options);
    video.addEventListener('emptied', () => {
      this.resetSeekState(card);
      this.resetVolumeState(card);
    }, options);
  }

  updateSeekState(card, video) {
    const input = card?.querySelector('[data-video-action="seek"]');
    if (!(input instanceof HTMLInputElement)) return;

    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    if (!duration) {
      this.resetSeekState(card);
      return;
    }

    const currentTime = Math.min(Math.max(video.currentTime || 0, 0), duration);
    const progress = (currentTime / duration) * 100;
    input.disabled = false;
    input.max = String(duration);
    input.value = String(currentTime);
    input.style.setProperty('--po-video-seek-progress', `${progress}%`);
    input.setAttribute(
      'aria-valuetext',
      `${this.formatSeconds(currentTime)} of ${this.formatSeconds(duration)}`
    );
  }

  resetSeekState(card) {
    const input = card?.querySelector('[data-video-action="seek"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.disabled = true;
    input.max = '0';
    input.value = '0';
    input.style.setProperty('--po-video-seek-progress', '0%');
    input.setAttribute('aria-valuetext', '0 seconds of 0 seconds');
  }

  setVolumeState(card, video) {
    const input = card?.querySelector('[data-video-action="volume"]');
    if (!(input instanceof HTMLInputElement)) return;

    const muted = video.muted || video.volume === 0;
    const displayedVolume = muted ? 0 : video.volume;
    if (!muted && video.volume > 0) input.dataset.lastVolume = String(video.volume);

    input.disabled = false;
    input.value = String(displayedVolume);
    input.style.setProperty('--po-video-volume-progress', `${displayedVolume * 100}%`);
    input.setAttribute('aria-valuetext', muted ? 'Muted' : `${Math.round(video.volume * 100)} percent`);
  }

  resetVolumeState(card) {
    const input = card?.querySelector('[data-video-action="volume"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.disabled = true;
    input.value = '0';
    input.dataset.lastVolume = '0.5';
    input.style.setProperty('--po-video-volume-progress', '0%');
    input.setAttribute('aria-valuetext', 'Muted');
  }

  formatSeconds(time) {
    const seconds = Math.max(0, Math.round(time));
    return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  }

  enforceHydrationLimit(protectedCard) {
    const hydratedCards = this.cards.filter((card) => card.querySelector('video'));
    if (hydratedCards.length <= MAX_HYDRATED_VIDEOS) return;

    hydratedCards
      .filter((card) => card !== protectedCard && card !== this.activeCard)
      .sort((a, b) => (this.hydratedAt.get(a) || 0) - (this.hydratedAt.get(b) || 0))
      .slice(0, hydratedCards.length - MAX_HYDRATED_VIDEOS)
      .forEach((card) => this.removeVideo(card));
  }

  pauseCard(card) {
    if (!(card instanceof HTMLElement)) return;

    const video = card.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.pause();
      video.muted = true;
      this.setVolumeState(card, video);
    }
    this.setPlayState(card, false);
    this.setMuteState(card, true);
  }

  pauseAll() {
    this.cards?.forEach((card) => this.pauseCard(card));
  }

  unloadAll() {
    this.cards?.forEach((card) => this.removeVideo(card));
    this.hydratedAt?.clear();
  }

  removeVideo(card) {
    const video = card?.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      this.mediaControllers?.get(video)?.abort();
      this.mediaControllers?.delete(video);
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
      video.remove();
    }
    card?.classList.remove('is-ready', 'is-loading');
    this.setPlayState(card, false);
    this.setMuteState(card, true);
    this.resetSeekState(card);
    this.resetVolumeState(card);
    this.hydratedAt?.delete(card);
  }

  setErrorState(card) {
    card.classList.remove('is-loading', 'is-ready');
    card.classList.add('has-error');
    card.dataset.userPaused = 'true';
    this.setPlayState(card, false, true);
    this.setStatus(card, 'Video could not load. Press play to retry.');
  }

  setPlayState(card, playing, retry = false) {
    const button = card?.querySelector('[data-video-action="play"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.setAttribute('aria-pressed', String(playing));
    const baseLabel = button.getAttribute('aria-label') || 'video';
    const videoLabel = baseLabel
      .replace(/^(Play|Pause|Retry) /, '')
      .replace(/^loading /i, '');
    button.setAttribute('aria-label', `${retry ? 'Retry' : playing ? 'Pause' : 'Play'} ${videoLabel}`);
  }

  setMuteState(card, muted) {
    const button = card?.querySelector('[data-video-action="mute"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.setAttribute('aria-pressed', String(muted));
    button.setAttribute('aria-expanded', String(!muted));
    const baseLabel = button.getAttribute('aria-label') || 'video';
    const videoLabel = baseLabel.replace(/^(Mute|Unmute) /, '');
    button.setAttribute('aria-label', `${muted ? 'Unmute' : 'Mute'} ${videoLabel}`);

    const volumeControl = card?.querySelector('.po-video-reviews__volume-control');
    const volumePopover = card?.querySelector('.po-video-reviews__volume-popover');
    if (volumeControl instanceof HTMLElement) volumeControl.dataset.volumeOpen = String(!muted);
    if (volumePopover instanceof HTMLElement) volumePopover.setAttribute('aria-hidden', String(muted));
  }

  setStatus(card, message) {
    const status = card?.querySelector('[data-video-status]');
    if (status) status.textContent = message;
  }

  handleControlClick = (event) => {
    const button = event.target.closest('[data-video-action]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const card = button.closest('.po-video-reviews__card');
    if (!(card instanceof HTMLElement)) return;

    const action = button.dataset.videoAction;
    if (action === 'play') {
      event.preventDefault();
      this.activeCard = card;
      const video = card.querySelector('video');
      if (video instanceof HTMLVideoElement && !video.paused) {
        video.pause();
        card.dataset.userPaused = 'true';
        this.setPlayState(card, false);
        this.setStatus(card, 'Video paused');
      } else {
        card.dataset.userPaused = 'false';
        this.playCard(card);
      }
    }

    if (action === 'mute') {
      event.preventDefault();
      const video = card.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        const volumeInput = card.querySelector('[data-video-action="volume"]');
        const shouldUnmute = video.muted || video.volume === 0;
        if (shouldUnmute) {
          const lastVolume =
            volumeInput instanceof HTMLInputElement ? Number(volumeInput.dataset.lastVolume) || 0.5 : 0.5;
          video.volume = Math.min(Math.max(lastVolume, 0.05), 1);
          video.muted = false;
        } else {
          if (volumeInput instanceof HTMLInputElement && video.volume > 0) {
            volumeInput.dataset.lastVolume = String(video.volume);
          }
          video.muted = true;
        }
        this.setMuteState(card, video.muted);
        this.setVolumeState(card, video);
        this.setStatus(card, video.muted ? 'Video muted' : 'Video sound on');
      } else {
        card.dataset.userPaused = 'false';
        this.playCard(card, { unmute: true });
      }
    }
  };

  handleRangeInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;

    const card = input.closest('.po-video-reviews__card');
    const video = card?.querySelector('video');
    if (!(card instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;

    if (input.dataset.videoAction === 'seek') {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      video.currentTime = Math.min(Math.max(Number(input.value) || 0, 0), duration);
      this.updateSeekState(card, video);
      this.setStatus(card, `Video position ${this.formatSeconds(video.currentTime)}`);
    }

    if (input.dataset.videoAction === 'volume') {
      const volume = Math.min(Math.max(Number(input.value) || 0, 0), 1);
      video.volume = volume;
      video.muted = volume === 0;
      if (volume > 0) input.dataset.lastVolume = String(volume);
      this.setMuteState(card, video.muted);
      this.setVolumeState(card, video);
      this.setStatus(card, video.muted ? 'Video muted' : `Video volume ${Math.round(volume * 100)} percent`);
    }
  };

  handleDocumentVisibility = () => {
    if (document.hidden) {
      this.pauseAll();
    } else {
      this.selectMostVisible();
    }
  };

  handleMotionPreferenceChange = () => {
    if (prefersReducedMotion()) {
      this.pauseAll();
    } else {
      this.selectMostVisible();
    }
  };

  handleBlockSelect = (event) => {
    const card = event.target?.closest?.('.po-video-reviews__card');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;

    this.activeCard = card;
    this.pauseAll();
    card.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
  };

  handleScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.updateOverflowState();
    });
  };

  updateOverflowState = () => {
    if (!this.track) return;

    const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const tolerance = 2;
    const hasOverflow = maximumScroll > tolerance;
    const atStart = this.track.scrollLeft <= tolerance;
    const atEnd = this.track.scrollLeft >= maximumScroll - tolerance;

    this.dataset.overflow = String(hasOverflow);
    this.dataset.atStart = String(atStart);
    this.dataset.atEnd = String(atEnd);
    if (this.previousButton instanceof HTMLButtonElement) this.previousButton.disabled = !hasOverflow || atStart;
    if (this.nextButton instanceof HTMLButtonElement) this.nextButton.disabled = !hasOverflow || atEnd;
  };

  moveBy(direction) {
    const currentIndex = Math.max(0, this.cards.indexOf(this.activeCard));
    const nextIndex = Math.min(this.cards.length - 1, Math.max(0, currentIndex + direction));
    const card = this.cards[nextIndex];
    card?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  handleTrackKeydown = (event) => {
    if (event.target instanceof Element && event.target.closest('input, button, a')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.moveBy(event.key === 'ArrowLeft' ? -1 : 1);
  };

  handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('button, a, input')) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.track.scrollLeft,
      dragged: false,
    };
    this.track.setPointerCapture(event.pointerId);
    this.track.classList.add('is-dragging');
  };

  handlePointerMove = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    const distance = event.clientX - this.dragState.startX;
    if (Math.abs(distance) > 5) this.dragState.dragged = true;
    if (!this.dragState.dragged) return;

    event.preventDefault();
    this.track.scrollLeft = this.dragState.startScrollLeft - distance;
  };

  handlePointerUp = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    this.suppressClick = this.dragState.dragged;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.dragState = null;
    this.track.classList.remove('is-dragging');
  };

  handleSuppressedClick = (event) => {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.suppressClick = false;
  };
}

if (!customElements.get('petoptimum-video-scroll-component')) {
  customElements.define('petoptimum-video-scroll-component', PetOptimumVideoScroll);
}
